import {$qs} from '../../../../@shared/ts/dom';

class StripeACHMandateElement {
	private readonly storeName: Readonly<string>;
	private readonly customerFullName: Readonly<string> | null;

	private mandateElement: HTMLDivElement | null;
	private mandateElementAccept: HTMLButtonElement | null;
	private mandateElementCancel: HTMLButtonElement | null;
	private mandateElementX: HTMLButtonElement | null;

	constructor(storeName: string, customerFullName: string | null) {
		this.storeName = storeName;
		this.customerFullName = customerFullName;

		this.mandateElement = null;
		this.mandateElementAccept = null;
		this.mandateElementCancel = null;
		this.mandateElementX = null;
	}

	/**
     * Prompts the customer to confirm the ACH mandate or not. Returns a boolean to indicate the response.
     */
	public async confirm(): Promise<boolean> {
		return new Promise((res, rej) => {
			if (!this.mount()) {
				this.unmount();
				rej(new Error('Developer error: Mandate failed to be mounted.'));
				return;
			}

			this.mandateElementAccept?.addEventListener('click', () => {
				this.unmount();
				res(true);
			});

			this.mandateElementCancel?.addEventListener('click', () => {
				this.unmount();
				res(false);
			});

			this.mandateElementX?.addEventListener('click', () => {
				this.unmount();
				res(false);
			});
		});
	}

	/**
     * Mounts and displays the mandate element. Returns a boolean to indicate success.
     */
	private mount(): boolean {
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = this.renderTemplate();

		this.mandateElement = tempDiv.querySelector('#ach-mandate-container');
		if (!this.mandateElement) {
			return false;
		}

		$qs('body', $el => $el.insertAdjacentElement('beforeend', this.mandateElement!));

		this.mandateElementAccept = this.mandateElement.querySelector('#ach-button-accept');
		if (!this.mandateElementAccept) {
			return false;
		}

		this.mandateElementCancel = this.mandateElement.querySelector('#ach-button-cancel');
		if (!this.mandateElementCancel) {
			return false;
		}

		this.mandateElementX = this.mandateElement.querySelector('#ach-button-x');
		if (!this.mandateElementX) {
			return false;
		}

		return true;
	}

	private unmount() {
		this.mandateElement?.remove();

		this.mandateElement = null;
		this.mandateElementAccept = null;
		this.mandateElementCancel = null;
		this.mandateElementX = null;
	}

	private renderTemplate(): string {
		const date = new Date().toLocaleDateString();
		const nameHtml = this.customerFullName === null ? '' : `<p>Name: ${this.customerFullName}</p>`;

		const html = /* html */ `
<div id="ach-mandate-container">
    <div id="ach-mandate-modal">
        <div id="ach-mandate-header">
            <button type="button" id="ach-button-x" class="txt-btn" style="font-size: 2rem;">×</button>
        </div>
        <div id="ach-mandate-body">
            <span>
                ${nameHtml}
                <p>Date: ${date}</p>
            </span>
            <br>
            <p>
                By clicking <b>Accept</b>, you authorize <span class="client">${this.storeName}</span> to debit the bank account specified above for any amount owed
                for charges arising from your use of <span class="client">${this.storeName}'s</span> services and/or purchase of products from
                <span class="client">${this.storeName}</span>, pursuant to <span class="client">${this.storeName}'s</span> website and terms, until this authorization
                is revoked. You may amend or cancel this authorization at any time by providing notice to <span class="client">${this.storeName}</span>with 30
                (thirty) days notice.
            </p>
            <br>
            <div style="text-align: center;">
                <button type="button" id="ach-button-accept" class="btn" style="padding: 9px; width: 7rem;">Accept</button>
                <button type="button" id="ach-button-cancel" class="txt-btn">Cancel</button>
            </div>
        </div>
    </div>
    <style>
        #ach-mandate-container{
            position: fixed;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            background-color: rgba(0,0,0,0.5);
            animation-name: fadein;
            animation-duration: 0.4s;
            z-index: 1000000000000;
        }

        @keyframes fadein{
            from {
                background-color: rgba(0,0,0,0);
            }
            to {
                background-color: rgba(0,0,0,0.5);
            }
        }

        #ach-mandate-container .client{
            border: 1px solid #e6e6e6;
            border-radius: 0.2rem;
            background-color: #e6e6e6;
            padding: 0 4px;
            margin-bottom: 1px;
            display: inline-block;
        }

        #ach-mandate-modal{
            position: absolute;
            background-color: white;
            border-radius: 0.4rem;
            z-index: 10000000000;
            width: 50vw;
            box-shadow:  2px 8px 20px 3px rgb(0 0 0 / 20%);
            margin: 0 calc(49vw / 2);
            top: 10vh;
        }

        #ach-mandate-header{
            padding: 0 0.5rem;
            text-align: right;
            border-bottom: 1px solid #e6e6e6;
        }

        #ach-mandate-body{
            padding: 1.5rem;
        }

        #ach-mandate-body p {
            margin: 0;
            padding: 0;
        }
        
        #ach-mandate-modal .txt-btn{
            border: none;
            background: none;
            color: grey;
            font-weight: 400;
            padding: 0.1rem 1rem 0.1rem 0 !important;
        }

        #ach-mandate-modal .txt-btn:hover{
            cursor: pointer;
            color: red;
        } 

        @media screen and (max-width: 768px) {

            
            #ach-mandate-modal{
                width: 100%;
                margin: 0;
                left: 0;
                right: 0;

                animation-duration: 0.8s;
                animation-name: slideup;
                top: unset;
                bottom: 0;
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }

            @keyframes slideup{
                from {
                    bottom: -100%
                }
                to {
                    bottom: 0;
                }
            }

        }

        /*This prevents the background from being scrollable*/
        body{
            overflow: hidden !important;
            touch-action: none !important;
        }
    </style>
</div>`;

		return html;
	}
}

export {
	StripeACHMandateElement,
};
